import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';

// ?facing=1 — WHICH WAY DOES THE CODE THINK EVERYTHING FACES (2026-10-31).
//
// Khari: "objects and people are often facing reverse of their intended
// positions … We will need a way to define a target direction or global spatial
// understanding". The convention is city/facing.js: a thing's front is its
// local +z. This draws that front as an arrow on everything that declares one
// (`userData.faces`: bodies, benches, props), so a reversal can be told apart
// at a glance:
//
//   the arrow points the wrong way          → the PLACEMENT is wrong (data, a
//                                              call site's angle)
//   the arrow is right, the thing faces     → the MODEL is built front −z;
//   away from it                              correct it once, where it loads
//
// Cyan = a body, yellow = furniture or a prop. Re-read twice a second, so
// people walking about keep their arrows.

const SHOW = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('facing') != null;
const LEN = { body: 0.7, prop: 0.9 };
const COLOR = { body: new THREE.Color('#00e5ff'), prop: new THREE.Color('#ffd400') };
const EVERY = 0.5;

const m = new THREE.Matrix4();
const pos = new THREE.Vector3();
const dir = new THREE.Vector3();
const quat = new THREE.Quaternion();
const scl = new THREE.Vector3();

function arrow(out, cols, p, d, kind) {
  const L = LEN[kind] || LEN.prop; const c = COLOR[kind] || COLOR.prop;
  d.y = 0; if (d.lengthSq() < 1e-8) return; d.normalize();
  const y = p.y + 0.05;
  const tx = p.x + d.x * L; const tz = p.z + d.z * L;
  // shaft, then two barbs at ±150°
  out.push(p.x, y, p.z, tx, y, tz);
  for (const a of [2.6, -2.6]) {
    const c2 = Math.cos(a); const s2 = Math.sin(a);
    out.push(tx, y, tz, tx + (d.x * c2 + d.z * s2) * L * 0.25, y, tz + (-d.x * s2 + d.z * c2) * L * 0.25);
  }
  for (let i = 0; i < 6; i++) cols.push(c.r, c.g, c.b);
}

export default function FacingOverlay() {
  const scene = useThree((s) => s.scene);
  const lines = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const l = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ vertexColors: true, depthTest: false, transparent: true }));
    l.renderOrder = 1000; l.frustumCulled = false; l.userData.facingOverlay = true;
    return l;
  }, []);
  useEffect(() => {
    scene.add(lines);
    return () => { scene.remove(lines); lines.geometry.dispose(); lines.material.dispose(); };
  }, [scene, lines]);

  const t = useRef(EVERY);
  useFrame((_, dt) => {
    t.current += dt;
    if (t.current < EVERY) return;
    t.current = 0;
    const out = []; const cols = [];
    scene.traverseVisible((o) => {
      const kind = o.userData?.faces;
      if (!kind) return;
      const k = kind === true ? 'prop' : kind;
      if (o.isInstancedMesh) {
        for (let i = 0; i < o.count; i++) {
          o.getMatrixAt(i, m); m.premultiply(o.matrixWorld);
          m.decompose(pos, quat, scl);
          arrow(out, cols, pos, dir.set(0, 0, 1).applyQuaternion(quat), k);
        }
      } else {
        o.getWorldPosition(pos);
        arrow(out, cols, pos, o.getWorldDirection(dir), k);
      }
    });
    lines.geometry.setAttribute('position', new THREE.Float32BufferAttribute(out, 3));
    lines.geometry.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
    lines.geometry.computeBoundingSphere();
  });
  return null;
}

export const FACING_OVERLAY = SHOW;
